import * as React from 'react';
import { Autocomplete, TextField, Chip, Box } from '@mui/material';

interface DropdownOption {
  label: string;
  value: string;
}

interface CreatableSelectProps {
  // Updated to support both single and multi-select
  value: DropdownOption[] | DropdownOption | null;
  onChange: (value: DropdownOption[] | DropdownOption | null) => void;
  placeholder?: string;
  size?: 'small' | 'medium';
  width?: number | string;
  options?: DropdownOption[];
  // New prop to control single vs multi select
  multiple?: boolean;
  isdisabled?: boolean; // New prop to disable the select
  isclearable?: boolean; // New prop to control clearability
}

const CreatableSelect: React.FC<CreatableSelectProps> = ({
  value,
  onChange,
  placeholder = 'Start typing...',
  size = 'medium',
  width = '100%',
  options = [],
  multiple = true,
  isdisabled, // Default to false
  isclearable = true, // Default to true
}) => {
  const getOption = (input: string): DropdownOption => {
    const existing = options.find((opt) => opt.label === input || opt.value === input);
    return existing || { label: input, value: input };
  };

  return (
    <Box sx={{ width }}>
      <Autocomplete
        multiple={multiple}
        disableClearable={multiple} // Only disable clear for multi-select
        filterSelectedOptions={multiple}
        options={options}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
        value={value}
        onChange={(_, newValue) => {
          if (multiple) {
            // Multi-select logic (existing)
            const mapped = (newValue as (string | DropdownOption)[]).map((val) =>
              typeof val === 'string' ? getOption(val) : val
            );
            onChange(mapped);
          } else {
            // Single-select logic
            if (newValue === null) {
              onChange(null);
            } else if (typeof newValue === 'string') {
              onChange(getOption(newValue));
            } else {
              onChange(newValue as DropdownOption);
            }
          }
        }}
        disabled={isdisabled}
        clearOnBlur={isclearable}
        renderTags={
          multiple
            ? (selected, getTagProps) =>
                (selected as DropdownOption[]).map((option, index) => (
                  <Chip label={option.label} {...getTagProps({ index })} color="primary" />
                ))
            : undefined
        }
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={placeholder}
            size={size}
            onKeyDown={(event) => {
              const inputValue = params.inputProps.value;
              if (
                event.key === 'Enter' &&
                typeof inputValue === 'string' &&
                inputValue.trim() !== ''
              ) {
                const match = options.find(
                  (opt) => opt.label.toLowerCase() === inputValue.toLowerCase()
                );

                if (match) {
                  if (multiple) {
                    // Multi-select: only add if not already selected
                    const currentValues = value as DropdownOption[];
                    if (!currentValues.find((val) => val.value === match.value)) {
                      onChange([...currentValues, match]);
                    }
                  } else {
                    // Single-select: replace current value
                    onChange(match);
                  }
                  event.preventDefault();
                }
              }
            }}
          />
        )}
      />
    </Box>
  );
};

export default CreatableSelect;
